import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from "axios";
import { TBENames, TBEVersion } from "./Axios.type";
import * as BE from "../../constants/axios/AxiosConstants";
import {
  getTokens,
  getNewTokensUsingRefresh,
  setNewTokens,
  reTryRequest,
  clearStorageAndRedirectToLogin,
  shouldNotRetry,
} from "../../utils/auth/getTokensLocalStorage";
import {
  FRANCHISE_HEADER,
  getFranchiseHeaderValue,
} from "../../utils/franchise/franchiseSlug";

export class AxiosFactory {
  private static headers = {
    "Content-Type": "application/json",
    "Access-Control-Allow-Origin": "*",
    Accept: "*/*",
  };

  public static getInstance(
    type: TBENames,
    version: TBEVersion,
  ): AxiosInstance {
    return AxiosFactory.createAxiosInstance(type, version);
  }

  private static createAxiosInstance = (
    type: TBENames,
    version: TBEVersion,
  ): AxiosInstance => {
    const instance = axios.create({
      baseURL: AxiosFactory.getUrl(type, version),
      timeout: 120000,
      headers: AxiosFactory.headers,
    });
    instance.interceptors.request.use(AxiosFactory.requestInterceptor);
    instance.interceptors.response.use(
      AxiosFactory.responseInterceptor,
      AxiosFactory.responseErrorInterceptor,
    );

    return instance;
  };

  private static getUrl(type: TBENames, version: TBEVersion): string {
    let URL;
    switch (type) {
      case BE.API_LAVORIS_URL:
        URL = `${process.env.REACT_APP_API_LAVORIS_URL
          }${AxiosFactory.getVersion(version)}`;
        break;
      case BE.B:
        URL = "/*GET URL FROM VARIABLES https:b.com*/";
        break;
      default:
        URL = `${process.env.REACT_APP_API_LAVORIS_URL
          }${AxiosFactory.getVersion(version)}`;
    }
    return URL;
  }

  private static getVersion(version: TBEVersion): string {
    let V;
    switch (version) {
      case BE.V1:
        V = "v1";
        break;

      default:
        V = BE.V1;
        break;
    }
    return V;
  }

  /**
   * Siempre retorna la timezone de Argentina
   * El sistema está configurado para funcionar con la timezone de Argentina,
   * independientemente de la ubicación del usuario
   */
  private static getUserTimezone(): string {
    return "America/Argentina/Buenos_Aires";
  }

  private static async requestInterceptor(
    request: AxiosRequestConfig,
  ): Promise<AxiosRequestConfig> {
    const tokens = getTokens();
    const userTimezone = AxiosFactory.getUserTimezone();
    const franchiseSlug = getFranchiseHeaderValue();

    return {
      ...request,
      data: request.data,
      headers: {
        ...request.headers,
        Authorization: `Bearer ${tokens?.access_token}`,
        "x-timezone": userTimezone,
        ...(franchiseSlug ? { [FRANCHISE_HEADER]: franchiseSlug } : {}),
      },
    };
  }

  private static async responseInterceptor(
    response: AxiosResponse,
  ): Promise<any> {
    let responseData;
    try {
      responseData = response.data;
    } catch (e) {
      console.error("Unexpected response error", e);
    }
    return responseData;
  }

  private static async responseErrorInterceptor(error: any) {
    try {
      if (shouldNotRetry(error.response.data)) return Promise.reject(error);

      const newTokensFromTheDb = await getNewTokensUsingRefresh();

      setNewTokens(newTokensFromTheDb.data);

      const userTimezone = AxiosFactory.getUserTimezone();
      const franchiseSlug = getFranchiseHeaderValue();
      const newHeaders = {
        ...AxiosFactory.headers,
        Authorization: `Bearer ${newTokensFromTheDb.data.access_token}`,
        "x-timezone": userTimezone,
        ...(franchiseSlug ? { [FRANCHISE_HEADER]: franchiseSlug } : {}),
      };

      return reTryRequest(error.config, newHeaders);
    } catch (error: any) {
      return clearStorageAndRedirectToLogin(error);
    }
  }
}

export default AxiosFactory;
